/// <amd-module name="Wit/Common/BacklogContentService/BacklogWorkItemConfigurationService" />

import { ObservableArray } from "VSS/Core/Observable";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { WorkItemTypeColorAndIcon } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { WorkItemStateAndColor } from "Wit/UI/WorkItemState";

namespace backlogDataProviders {
    export const workItemStateAndColorDataProvider = "ms.vss-work-web.work-item-states-color-data-provider";
    export const workItemTypeColorAndIconsDataProvider = "ms.vss-work-web.work-item-type-color-icon-data-provider";
}

export interface IBacklogWorkItemConfigurationService extends IVssService {
    getObservableWorkItemTypeColorAndIcons(): ObservableArray<WorkItemTypeColorAndIcon>;
    getWorkItemStateAndColorDictionary(options?: { ignoreCache?: boolean }): Promise<{ [workItemType: string]: WorkItemStateAndColor[] }>;
    getWorkItemTypeColorAndIcons(options?: { ignoreCache?: boolean }): Promise<WorkItemTypeColorAndIcon[]>;
}

class BacklogWorkItemConfigurationService extends VssService implements IBacklogWorkItemConfigurationService {
    private observableWorkItemTypeColorAndIcons = new ObservableArray<WorkItemTypeColorAndIcon>([]);
    private workItemStateAndColorDictionaryPromise: { [workItemType: string]: WorkItemStateAndColor[] } | undefined;
    private workItemTypeColorAndIconsPromise: Promise<WorkItemTypeColorAndIcon[] | undefined>;

    public getObservableWorkItemTypeColorAndIcons(): ObservableArray<WorkItemTypeColorAndIcon> {
        return this.observableWorkItemTypeColorAndIcons;
    }

    public async getWorkItemStateAndColorDictionary(): Promise<{ [workItemType: string]: WorkItemStateAndColor[] }> {
        return (await this.ensureWorkItemStateAndColorDictionary()) ?? {};
    }

    public async getWorkItemTypeColorAndIcons(): Promise<WorkItemTypeColorAndIcon[]> {
        return (await this.ensureWorkItemTypeColorAndIcons()) ?? [];
    }

    private async ensureWorkItemStateAndColorDictionary(): Promise<{ [workItemType: string]: WorkItemStateAndColor[] } | undefined> {
        if (!this.workItemStateAndColorDictionaryPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.workItemStateAndColorDictionaryPromise = await contributionService.getDataAsync<{ [workItemType: string]: WorkItemStateAndColor[] }>(
                backlogDataProviders.workItemStateAndColorDataProvider
            );

            // @NOTE: fixup all the data from the data providers to ensure there is a fill color
            if (this.workItemStateAndColorDictionaryPromise) {
                for (const name in this.workItemStateAndColorDictionaryPromise) {
                    const wisc = this.workItemStateAndColorDictionaryPromise[name];
                    for (let index = 0; index < wisc.length; index++) {
                        wisc[index].fill = (wisc[index] as any).color;
                    }
                }
            }
        }

        return this.workItemStateAndColorDictionaryPromise;
    }

    private async ensureWorkItemTypeColorAndIcons(): Promise<WorkItemTypeColorAndIcon[] | undefined> {
        if (!this.workItemTypeColorAndIconsPromise) {
            const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
            this.workItemTypeColorAndIconsPromise = contributionService.getDataAsync<WorkItemTypeColorAndIcon[]>(
                backlogDataProviders.workItemTypeColorAndIconsDataProvider
            );

            this.observableWorkItemTypeColorAndIcons.value = (await this.workItemTypeColorAndIconsPromise) ?? [];
        }

        return this.workItemTypeColorAndIconsPromise;
    }
}

Services.add("IBacklogWorkItemConfigurationService", { serviceFactory: BacklogWorkItemConfigurationService });
